import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { Copy, Crown, Gem, HelpCircle, History, Scale, Users, X } from 'lucide-react'
import { toast } from 'react-hot-toast'
import useRoyaleStore from '../store/useRoyaleStore'
import gameSocket from '../../crash/services/crashSocket'
import useAppStore from '../../../store/useAppStore'
import useAudioManager from '../../../hooks/useAudioManager'
import RoyaleBetsFeed from '../components/RoyaleBetsFeed'
import MobileBetSheet from '../../../components/MobileBetSheet'
import RoyaleParticipantsShowcase from '../components/RoyaleParticipantsShowcase'
import RoyaleTeamsSummary from '../components/RoyaleTeamsSummary'
import RoyaleRoulette from '../components/RoyaleRoulette'
import RoyaleWinnerCard from '../components/RoyaleWinnerCard'
import RoyaleUserShare from '../components/RoyaleUserShare'
import HelpModal from '../../../components/HelpModal'
import { hapticImpact, hapticNotify } from '../../../utils/haptic'
import { sendYandexGoal } from '../../../services/yandexMetrikaService'
import TeamCapturePreview from '../../team/components/TeamCapturePreview'
import useUpdateBlocker from '../../../hooks/useUpdateBlocker'
import { isRoundSnapshotUpdateBlocked } from '../../../services/appUpdateSafety'

const formatTimerValue = (value) => {
  if (value === null || value === undefined) return '01:00'
  if (typeof value === 'string') return value
  const totalSeconds = Math.max(0, Math.floor(value))
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0')
  const seconds = String(totalSeconds % 60).padStart(2, '0')
  return `${minutes}:${seconds}`
}

const parseTimerValue = (value) => {
  if (typeof value !== 'string') return 0
  const [minutes, seconds] = value.split(':').map((part) => Number(part))
  if (Number.isNaN(minutes) || Number.isNaN(seconds)) return 0
  return Math.max(0, minutes * 60 + seconds)
}

const formatValue = (value, head = 6, tail = 6) => {
  if (!value) return '—'
  const text = String(value)
  if (text.length <= head + tail + 3) return text
  return `${text.slice(0, head)}...${text.slice(-tail)}`
}

const Royale = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const {
    activeRound,
    engine,
    isConnected,
    isModeDisabled,
    limits,
    bets,
    users,
    teams,
    isBetting,
    inventory,
    balance,
    pendingBetIds,
    hasConfirmedBetKey,
    addConfirmedBetKey,
    loadActiveRound,
    loadBetInventory,
    placeBet,
    removePendingBetId,
    setBalance
  } = useRoyaleStore()
  const { user, loadProfile, balance: appBalance, isGuest, openLogin } = useAppStore()
  const { playSound, stopSound } = useAudioManager({
    select: 'select',
    pressure: 'royaleTimerPressure',
    lastSeconds: 'royaleTimerLastSeconds'
  })

  const [isBetModalOpen, setIsBetModalOpen] = useState(false)
  const [isBetDataReady, setIsBetDataReady] = useState(false)
  const [isFairPlayOpen, setIsFairPlayOpen] = useState(false)
  const [timerLabel, setTimerLabel] = useState('')
  const [timerValue, setTimerValue] = useState('01:00')
  const [timerTotalSeconds, setTimerTotalSeconds] = useState(60)
  const [isTimerExpired, setIsTimerExpired] = useState(false)
  const [rouletteStartKey, setRouletteStartKey] = useState(0)
  const [rouletteRoundId, setRouletteRoundId] = useState(null)
  const [resultSnapshot, setResultSnapshot] = useState(null)
  const [showHelp, setShowHelp] = useState(false)
  const [showReconnectNotice, setShowReconnectNotice] = useState(false)
  useUpdateBlocker(!isGuest && (isBetting || pendingBetIds.length > 0
    || isRoundSnapshotUpdateBlocked({ activeRound, engine, isConnected, isModeDisabled })))

  const pollingRef = useRef(null)
  const timerRef = useRef(null)
  const abortControllerRef = useRef(null)
  const pendingSocketBetsRef = useRef(new Map())
  const betCounterRef = useRef(0)
  const roundSyncRef = useRef(null)
  // Абсолютный дедлайн текущей фазы таймера (ms, в системе Date.now()).
  // Вычисляется ОДИН раз при смене фазы (roundId + status) и не сбрасывается поллингом,
  // чтобы таймер тикал ровно на клиенте независимо от сетевых задержек.
  const deadlineAtRef = useRef(null)
  // Ключ текущей фазы таймера: `${roundId}:${status}`
  const timerPhaseKeyRef = useRef(null)
  const lastStatusRef = useRef(null)
  const lastRoundIdRef = useRef(null)
  const rouletteRoundRef = useRef(null)
  const errorToastRoundRef = useRef(null)
  // Флаг проигрывания зацикленного звука ожидания старта розыгрыша
  const pressurePlayingRef = useRef(false)
  // ID раунда, для которого уже был проигран финальный 3-2-1-0 звук
  const lastSecondsPlayedRoundIdRef = useRef(null)

  const handleStopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }

  const handleCopy = async (label, value) => {
    if (!value) {
      toast.error(t('royale.toasts.copyNoData'))
      return
    }

    try {
      await navigator.clipboard.writeText(String(value))
      toast.success(t('royale.toasts.copied', { label }))
    } catch (err) {
      console.error('Ошибка копирования:', err)
      toast.error(t('royale.toasts.copyFailed'))
    }
  }

  // Единая функция тика: считает остаток по абсолютному дедлайну и обновляет UI.
  // Никогда не сбрасывается поллингом → ровный счёт в браузере.
  const updateTimer = () => {
    if (!deadlineAtRef.current) return

    const remainingMs = deadlineAtRef.current - Date.now()
    const remaining = Math.max(0, Math.ceil(remainingMs / 1000))

    // Финальный отсчёт 3-2-1-0 на фазе 'started': проигрываем один раз за раунд.
    // Работает даже при refresh страницы в середине раунда — ref сбрасывается на mount,
    // и звук сработает, как только remaining станет ≤ 3.
    if (lastStatusRef.current === 'started') {
      const currentRoundId = lastRoundIdRef.current
      if (
        remaining <= 3
        && remaining > 0
        && currentRoundId
        && lastSecondsPlayedRoundIdRef.current !== currentRoundId
      ) {
        lastSecondsPlayedRoundIdRef.current = currentRoundId
        playSound('lastSeconds')
      }
    }

    if (remaining <= 0) {
      setTimerValue('00:00')
      setIsTimerExpired(true)
      deadlineAtRef.current = null
      return
    }

    setIsTimerExpired(false)
    setTimerValue(formatTimerValue(remaining))
  }

  // Настройка таймера на основе раунда с сервера.
  // Дедлайн фазы вычисляется ОДИН раз при смене (roundId + status) и не трогается,
  // пока фаза не изменится — это защищает от дёрганья таймера из-за сетевых задержек.
  const setupTimerFromRound = (round) => {
    if (!round) return

    const roundId = round.id
    const status = round.status
    const phaseKey = `${roundId}:${status}`
    const isPhaseChanged = timerPhaseKeyRef.current !== phaseKey

    lastStatusRef.current = status
    lastRoundIdRef.current = roundId

    setTimerLabel('')

    if (status === 'started') {
      const secondsToRaffle = round.ends_at && engine === 'node'
        ? Math.max(0, (round.ends_at - Date.now() - gameSocket.clockOffset) / 1000)
        : Number(round.second_to_raffle ?? 60)

      if (secondsToRaffle <= 0) {
        // Сервер прислал уже истёкший таймер — сразу ставим в ноль
        setIsTimerExpired(true)
        setTimerValue('00:00')
        deadlineAtRef.current = null
        if (isPhaseChanged) setTimerTotalSeconds(Math.max(1, secondsToRaffle))
      } else if (isPhaseChanged || (engine === 'node' && round.ends_at && deadlineAtRef.current !== round.ends_at - gameSocket.clockOffset)) {
        // Фаза сменилась — фиксируем абсолютный дедлайн и стартовый total
        setIsTimerExpired(false)
        deadlineAtRef.current = engine === 'node' && round.ends_at
          ? round.ends_at - gameSocket.clockOffset
          : Date.now() + secondsToRaffle * 1000
        if (isPhaseChanged) setTimerTotalSeconds(Number(round.second_round) || secondsToRaffle)
        setTimerValue(formatTimerValue(secondsToRaffle))
      }
      // Если фаза та же — не трогаем дедлайн/total, клиент тикает сам

      timerPhaseKeyRef.current = phaseKey
      return
    }

    if (status === 'waiting' || status === 'new') {
      if (isPhaseChanged) {
        setIsTimerExpired(false)
        deadlineAtRef.current = Date.now() + 60 * 1000
        setTimerTotalSeconds(60)
        setTimerValue('01:00')
      }
      timerPhaseKeyRef.current = phaseKey
      return
    }

    if (status === 'raffle') {
      if (isPhaseChanged) {
        setIsTimerExpired(false)
        deadlineAtRef.current = engine === 'node' && round.anim_ends_at
          ? round.anim_ends_at - gameSocket.clockOffset : Date.now() + 10 * 1000
        setTimerTotalSeconds(10)
        setTimerValue(formatTimerValue(Math.ceil((deadlineAtRef.current - Date.now()) / 1000)))
      }
      timerPhaseKeyRef.current = phaseKey
      return
    }

    if (status === 'result') {
      if (isPhaseChanged) {
        setIsTimerExpired(false)
        deadlineAtRef.current = engine === 'node' && round.result_ends_at
          ? round.result_ends_at - gameSocket.clockOffset : Date.now() + 5 * 1000
        setTimerTotalSeconds(5)
        setTimerValue(formatTimerValue(Math.ceil((deadlineAtRef.current - Date.now()) / 1000)))
      }
      timerPhaseKeyRef.current = phaseKey
      return
    }

    // Неизвестный статус — сбрасываем таймер
    if (isPhaseChanged) {
      deadlineAtRef.current = null
      setTimerTotalSeconds(0)
      setTimerValue('00:00')
    }
    timerPhaseKeyRef.current = phaseKey
  }

  const checkPendingBets = (round) => {
    if (!round || !round.bets) return

    const roundId = round.id || round.round_id || round.roundId
    const userChatId = String(user?.chat_id || '')
    const userBets = round.bets.filter((bet) =>
      bet.your_bet || (userChatId && String(bet.chat_id || '') === userChatId)
    )

    if (userBets.length > 0) {
      let hasNewAcceptedBet = false
      userBets.forEach((bet) => {
        const betKey = `${roundId}:${bet.id}`
        if (!hasConfirmedBetKey(betKey)) {
          addConfirmedBetKey(betKey)
          hasNewAcceptedBet = true
          toast.success(t('royale.toasts.betAccepted'))
        }
        if (pendingBetIds.includes(bet.id)) {
          removePendingBetId(bet.id)
        }
      })

      // Сервер списывает баланс и предметы только в момент принятия ставки.
      // Здесь сверяем оптимистичное состояние (баланс + инвентарь) с сервером.
      if (hasNewAcceptedBet) {
        handleLoadInventory()
      }
    }

    if (pendingBetIds.length === 0) {
      return
    }

    const foundIds = pendingBetIds.filter((id) =>
      round.bets.some((bet) => bet.id === id)
    )
    const remainingPendingIds = pendingBetIds.filter((id) => !foundIds.includes(id))

    if (foundIds.length > 0) {
      foundIds.forEach((id) => removePendingBetId(id))
    }

    if (remainingPendingIds.length === 0) {
      return
    }

    if (['raffle', 'result', 'finished'].includes(round.status)) {
      remainingPendingIds.forEach((id) => removePendingBetId(id))
      if (userBets.length === 0 && errorToastRoundRef.current !== roundId) {
        errorToastRoundRef.current = roundId
        toast.error(t('royale.toasts.betMissed'))
        // Ставка не принята — возвращаем баланс и предметы с сервера
        handleLoadInventory()
      }
    }
  }

  roundSyncRef.current = (round) => {
    setupTimerFromRound(round)
    if (engine === 'worker') checkPendingBets(round)
  }

  useEffect(() => {
    const pending = pendingSocketBetsRef.current
    let disposed = false
    let inventoryRequest = null
    let inventoryRefreshQueued = false
    const refreshInventory = () => {
      if (useAppStore.getState().isGuest) return
      if (inventoryRequest) {
        inventoryRefreshQueued = true
        return
      }
      inventoryRequest = useRoyaleStore.getState().loadBetInventory()
        .then((data) => { if (!disposed && data) setIsBetDataReady(true) })
        .catch(() => {})
        .finally(() => {
          inventoryRequest = null
          if (inventoryRefreshQueued && !disposed) {
            inventoryRefreshQueued = false
            refreshInventory()
          }
        })
    }
    const settle = (id, code) => {
      const entry = pending.get(String(id))
      if (!entry) return
      clearTimeout(entry.timer)
      pending.delete(String(id))
      useRoyaleStore.setState({ isBetting: pending.size > 0 })
      if (code) entry.reject(new Error(code))
      else {
        useRoyaleStore.setState((state) => ({
          inventory: state.inventory.filter((item) => !entry.items.includes(item.id)),
          pendingItemLocks: { ...state.pendingItemLocks, ...Object.fromEntries(entry.items.map((item) => [item, Date.now() + 5000])) }
        }))
        entry.resolve()
      }
    }
    const receiveRound = (round) => {
      if (useRoyaleStore.getState().engine !== 'node' || !round) return
      useRoyaleStore.setState({ activeRound: round, bets: round.bets || [], users: round.users || [], teams: round.teams || [], isModeDisabled: false, isConnected: true })
    }
    const handlers = {
      'classic:state': (data) => {
        useRoyaleStore.setState({ engine: data.engine, limits: data.limits, isModeDisabled: Boolean(data.disabled), isConnected: true })
        if (data.engine === 'node') {
          if (data.server_time) gameSocket.clockOffset = Number(data.server_time) - Date.now()
          useRoyaleStore.setState({ activeRound: data.round, bets: data.round?.bets || [], users: data.round?.users || [], teams: data.round?.teams || [] })
        } else useRoyaleStore.getState().loadActiveRound().catch(() => {})
      },
      'classic:round': receiveRound,
      'classic:bet:ok': (data) => settle(data.client_id),
      'classic:engine': ({ mode }) => {
        useRoyaleStore.setState({ engine: mode, isConnected: mode !== 'node' })
        if (mode === 'node') gameSocket.requestState('classic')
        else useRoyaleStore.getState().loadActiveRound().catch(() => {})
      },
      'classic:disabled': () => useRoyaleStore.setState({ isModeDisabled: true, activeRound: null, bets: [], users: [], teams: [] }),
      'user:balance': ({ balance }) => {
        if (gameSocket.authToken && gameSocket.resolveAuthToken() === gameSocket.authToken && balance != null && Number.isFinite(Number(balance))) useRoyaleStore.setState({ balance: Number(balance) })
      },
      'user:inventory': () => {
        if (gameSocket.authToken && gameSocket.resolveAuthToken() === gameSocket.authToken && useRoyaleStore.getState().engine === 'node') refreshInventory()
      },
      'user:alert': (data) => {
        if (data.game && data.game !== 'classic') return
        if (data.code === 'engine_switched') {
          useRoyaleStore.setState({ engine: 'worker' })
          useRoyaleStore.getState().loadActiveRound().catch(() => {})
        }
        if (data.code === 'round_not_found') gameSocket.emit('classic:state')
        if (data.client_id) settle(data.client_id, data.code)
        else if (pending.size === 1) settle(pending.keys().next().value, data.code)
      }
    }
    const onConnect = () => {
      useRoyaleStore.setState({ isConnected: useRoyaleStore.getState().engine !== 'node' })
    }
    const onDisconnect = () => {
      useRoyaleStore.setState({ isConnected: false })
      pending.forEach((_, id) => settle(id, 'disconnected'))
    }
    Object.entries(handlers).forEach(([event, handler]) => gameSocket.on(event, handler))
    gameSocket.onConnect(onConnect)
    gameSocket.onDisconnect(onDisconnect)
    gameSocket.subscribe('classic')
    if (gameSocket.isConnected()) onConnect()
    const controller = new AbortController()
    useRoyaleStore.getState().loadActiveRound(controller.signal).catch(() => {})
    return () => {
      disposed = true
      controller.abort()
      Object.entries(handlers).forEach(([event, handler]) => gameSocket.off(event, handler))
      gameSocket.offConnect(onConnect)
      gameSocket.offDisconnect(onDisconnect)
      gameSocket.unsubscribe('classic')
      pending.forEach((_, id) => settle(id, 'disconnected'))
      useRoyaleStore.setState({ isConnected: false })
    }
  }, [])

  useEffect(() => {
    if (engine !== 'node' || isConnected) {
      setShowReconnectNotice(false)
      return undefined
    }
    const timer = setTimeout(() => setShowReconnectNotice(true), 3000)
    return () => clearTimeout(timer)
  }, [engine, isConnected])

  useEffect(() => {
    if (engine !== 'worker') return undefined
    let loading = false
    const poll = async () => {
      if (loading) return
      loading = true
      const controller = new AbortController()
      abortControllerRef.current = controller
      await loadActiveRound(controller.signal).catch(() => {})
      loading = false
    }
    poll()
    pollingRef.current = setInterval(poll, isGuest ? 10000 : 1000)
    return () => handleStopPolling()
  }, [engine, isGuest, loadActiveRound])

  useEffect(() => {
    if (!activeRound) return
    roundSyncRef.current(activeRound)
  }, [activeRound, engine])

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    // Тикаем только пока идёт обратный отсчёт до розыгрыша
    if (activeRound?.status === 'started') {
      // Первый тик сразу, чтобы UI не ждал 250мс
      updateTimer()
      timerRef.current = setInterval(() => {
        updateTimer()
      }, 250)
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [activeRound?.status])

  useEffect(() => {
    if (!activeRound) return

    if (activeRound.status === 'raffle' && activeRound.id) {
      if (rouletteRoundRef.current !== activeRound.id) {
        // Запускаем рулетку один раз на раунд
        rouletteRoundRef.current = activeRound.id
        setRouletteRoundId(activeRound.id)
        setRouletteStartKey((prev) => prev + 1)
      }
      return
    }

    if (activeRound.status === 'new') {
      // Сбрасываем рулетку при старте нового раунда
      rouletteRoundRef.current = null
      setRouletteRoundId(null)
    }
  }, [activeRound?.status, activeRound?.id])

  useEffect(() => {
    if (!activeRound) return
    if (['result', 'finished'].includes(activeRound.status)) {
      setResultSnapshot(activeRound)
      return
    }
    if (activeRound.status === 'new') {
      setResultSnapshot(null)
    }
  }, [activeRound])

  // Зацикленный звук ожидания старта розыгрыша: играет, пока статус 'started' (батл начался)
  useEffect(() => {
    const isPressureStatus = activeRound?.status === 'started'

    if (isPressureStatus && !pressurePlayingRef.current) {
      playSound('pressure', null, { loop: true })
      pressurePlayingRef.current = true
      return
    }

    if (!isPressureStatus && pressurePlayingRef.current) {
      stopSound('pressure')
      pressurePlayingRef.current = false
    }
  }, [activeRound?.status, playSound, stopSound])

  // Остановка зацикленных звуков при уходе со страницы
  useEffect(() => {
    return () => {
      stopSound('pressure')
      pressurePlayingRef.current = false
    }
  }, [stopSound])

  const currentBalance = engine === 'node' || isBetDataReady ? Number(balance || 0) : Number(appBalance ?? user?.balance ?? 0)
  const minBet = Number(engine === 'node' ? limits?.min_bet ?? activeRound?.min_bet ?? 100 : activeRound?.min_bet ?? 100)
  const maxBet = engine === 'node' ? Number(limits?.max_bet ?? Infinity) : Infinity
  const maxItems = engine === 'node' ? Number(limits?.max_items ?? Infinity) : Infinity
  const isBettingDisabled = !engine || !(activeRound?.id || activeRound?.round_id || activeRound?.roundId) || isModeDisabled
    || ['raffle', 'result', 'finished'].includes(activeRound?.status)
    || (engine === 'node' && (!isConnected || maxBet < minBet
      || (activeRound?.ends_at && Date.now() + gameSocket.clockOffset >= activeRound.ends_at)))
  const totalBetValue = Number(activeRound?.total_bet || 0)
  const roundStatus = activeRound?.status || 'new'
  const resultData = resultSnapshot && ['result', 'finished'].includes(roundStatus)
    ? resultSnapshot
    : activeRound
  const isRoundCompleted = ['result', 'finished'].includes(roundStatus)
  const winner = resultData?.winner
  const highlightId = String(winner?.chat_id || winner?.id || winner?.name || '')
  const roundId = activeRound?.id || activeRound?.round_id || activeRound?.roundId
  const showStats = users.length > 0
  const statusLabels = {
    new: t('royale.status.new'),
    waiting: t('royale.status.waiting'),
    raffle: t('royale.status.raffle'),
    result: t('royale.status.result'),
    finished: t('royale.status.finished')
  }
  const isStarted = roundStatus === 'started'
  // Раунд, где все ставки сделали члены одной банды, не разыгрывается: ждём вторую сторону.
  // Без подсказки это читается как зависший раунд — в нём есть игроки, но нет таймера.
  const isWaitingOtherTeam = roundStatus === 'waiting' && Boolean(activeRound?.waiting_for_other_team)
  const remainingSeconds = isStarted ? parseTimerValue(timerValue) : 0
  const showTimer = isStarted && !isTimerExpired && remainingSeconds > 0
  const statusLabel = isStarted && (isTimerExpired || remainingSeconds <= 0)
    ? statusLabels.raffle
    : isWaitingOtherTeam
      ? t('royale.status.waitingOtherTeam')
      : statusLabels[roundStatus]
  const statusClassName = roundStatus === 'waiting'
    ? 'bg-accent-primary/15 text-accent-primary border border-accent-primary/30'
    : 'bg-white/10 text-white/80 border border-white/10'
  const roundHash = activeRound?.hash
  const totalSeconds = Math.max(1, Number(timerTotalSeconds || 0))
  const progressRatio = showTimer
    ? Math.min(1, Math.max(0, remainingSeconds / totalSeconds))
    : 0

  const handleOpenBetModal = () => {
    if (isGuest) return openLogin()
    if (isBettingDisabled) return
    playSound('select')
    hapticImpact('medium')
    handleLoadInventory()
    setIsBetModalOpen(true)
  }

  const handleCloseBetModal = () => {
    setIsBetModalOpen(false)
  }

  const handleLoadInventory = useCallback(async () => {
    if (isGuest) return
    try {
      if (useRoyaleStore.getState().engine !== 'node') {
        try {
          await loadProfile()
        } catch (error) {
          console.error('Ошибка обновления профиля перед ставкой:', error)
        }
      }

      const inventoryData = await loadBetInventory()
      if (!inventoryData) return

      // Синхронизируем баланс из appStore, т.к. loadProfile уже обновил его
      if (useRoyaleStore.getState().engine !== 'node') {
        const appStore = useAppStore.getState()
        const syncBalance = appStore.balance ?? appStore.user?.balance
        if (syncBalance != null) setBalance(Number(syncBalance))
      }
      setIsBetDataReady(true)
    } catch (error) {
      if (error.message === 'error backend') {
        toast.error(t('royale.toasts.inventoryUnavailable'))
      } else {
        toast.error(t('royale.toasts.inventoryLoadError'))
      }
    }
  }, [isGuest, loadBetInventory, loadProfile, setBalance, t])

  useEffect(() => {
    if (engine) handleLoadInventory()
  }, [engine, handleLoadInventory])

  const handleSubmitBet = async ({ crystal, items }) => {
    if (!roundId) {
      hapticNotify('error')
      toast.error(t('royale.toasts.roundUnavailable'))
      return false
    }

    try {
      if (engine === 'node') {
        if (!isConnected || !gameSocket.isConnected() || useRoyaleStore.getState().activeRound?.id !== roundId
          || (activeRound?.ends_at && Date.now() + gameSocket.clockOffset >= activeRound.ends_at)) throw new Error('round_closed')
        if (items.some((id) => !inventory.some((item) => item.id === id))) throw new Error('items_not_available')
        const clientId = `${Date.now().toString(36)}-${(++betCounterRef.current).toString(36)}-${Math.random().toString(36).slice(2, 9)}`
        await new Promise((resolve, reject) => {
          const timer = setTimeout(() => {
            pendingSocketBetsRef.current.delete(clientId)
            useRoyaleStore.setState({ isBetting: pendingSocketBetsRef.current.size > 0 })
            gameSocket.emit('classic:state')
            reject(new Error('bet_unconfirmed'))
          }, 3000)
          pendingSocketBetsRef.current.set(clientId, { resolve, reject, timer, items })
          useRoyaleStore.setState({ isBetting: true })
          gameSocket.emit('classic:bet', { round_id: roundId, crystal: Math.floor(Number(crystal) || 0), items, client_id: clientId })
        })
        toast.success(t('royale.toasts.betAccepted'))
      } else {
        await placeBet({ roundId, crystal, items })
        // Баланс и инвентарь обновлены оптимистично в store; сверка с сервером
        // произойдёт в момент подтверждения ставки (checkPendingBets)
        await loadActiveRound()
      }
      sendYandexGoal('royale-bet')
      hapticNotify('success')
      setIsBetModalOpen(false)
      return true
    } catch (error) {
      if (error?.status === 409 || error?.message === 'engine_switched') {
        await loadActiveRound().catch(() => {})
        if (useRoyaleStore.getState().engine === 'node') gameSocket.requestState('classic')
        return false
      }
      hapticNotify('error')
      if (engine === 'node') {
        toast.error(t(`realtime.alerts.${error.message}`, t('royale.toasts.betFailed')))
        return false
      }
      if (error.message === 'not enough balance') {
        toast.error(
          <span className="inline-flex items-center gap-1">
            {t('royale.toasts.notEnoughBalance')} <Gem className="w-3.5 h-3.5 text-accent-primary" />
          </span>
        )
      } else if (error.message === 'error backend') {
        toast.error(t('royale.toasts.betFailed'))
      } else {
        toast.error(error.message || t('royale.toasts.betFailed'))
      }
      return false
    }
  }

  const resultUsers = resultData?.users || users
  const resultBets = resultData?.bets || bets
  const resultTotalBet = Number(resultData?.total_bet || totalBetValue)
  const currentUserId = String(user?.telegramid || user?.chat_id || user?.id || '')
  const isParticipant = resultBets.some((bet) => bet.your_bet
    || (currentUserId && String(bet.chat_id || bet.id) === currentUserId))
  const isCurrentUser = resultBets.some((bet) => bet.win_bet
    && (bet.your_bet || (currentUserId && String(bet.chat_id || bet.id) === currentUserId)))
    || Boolean(winner?.you)
    || (Boolean(currentUserId) && String(winner?.chat_id || winner?.id) === currentUserId)
  const showcaseUsers = isRoundCompleted ? resultUsers : users
  const showcaseTotalBet = isRoundCompleted ? resultTotalBet : totalBetValue
  // Индекс победителя в массиве, который реально уходит в showcase.
  // Надёжный fallback, когда у победителя пустые name/chat_id/id
  const highlightIndex = isRoundCompleted ? showcaseUsers.findIndex((u) => u?.win) : -1
  const winnerPercent = winner?.percent
    ?? resultUsers.find((user) => String(user.chat_id || user.id) === String(winner?.chat_id || winner?.id))?.percent
    ?? (resultTotalBet > 0 && winner?.total_crystal ? (Number(winner.total_crystal) / resultTotalBet) * 100 : null)
  const winnerAmount = resultData?.sent_win?.user_get_crystal ?? resultData?.total_bet
  const userBets = (isRoundCompleted ? resultBets : bets).filter((bet) =>
    bet.your_bet || (currentUserId && String(bet.chat_id || '') === currentUserId)
  )
  const userBetTotal = userBets.reduce((sum, bet) => sum + Number(bet.total_crystal || 0), 0)
  // Возврат как тиммейту победителя: у игрока может быть несколько ставок, поэтому
  // суммируем по bets[]; фолбэк на users[] — если ставок в ответе нет
  const userRefund = userBets.reduce((sum, bet) => sum + Number(bet.refund || 0), 0)
    || Number(resultUsers.find((participant) => String(participant.chat_id || '') === currentUserId)?.refund || 0)
  const showcaseTeams = isRoundCompleted ? (resultData?.teams || []) : teams
  const userSharePercent = showcaseTotalBet > 0 ? (userBetTotal / showcaseTotalBet) * 100 : null
  const userDisplaySource = userBets[0]
    || showcaseUsers.find((participant) => String(participant.chat_id || participant.id || '') === currentUserId)
  const userAvatar = userDisplaySource?.ava
  const userName = userDisplaySource?.name || user?.username || user?.name || ''

  const actionButtons = (
    <>
      {(roundId || roundHash) && (
        <button
          type="button"
          onClick={() => setIsFairPlayOpen(true)}
          className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-full bg-surface text-text-secondary hover:text-white border border-white/10 transition-colors"
          aria-label={t('royale.buttons.fairPlay')}
        >
          <Scale className="w-4 h-4" />
          <span className="text-sm">{t('royale.buttons.fairPlay')}</span>
        </button>
      )}

      <button
        onClick={() => navigate('/royale/history')}
        className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-full bg-surface text-text-secondary hover:text-white border border-white/10 transition-colors"
      >
        <History className="w-4 h-4" />
        <span className="text-sm">{t('royale.buttons.history')}</span>
      </button>
    </>
  )

  if (isModeDisabled || !engine) return (
    <div className="px-4 py-12 text-center text-text-secondary" role="status">
      {isModeDisabled ? t('realtime.disabled') : t('common.loading')}
    </div>
  )

  return (
    <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8">
      <div className="mx-auto max-w-7xl space-y-6 lg:space-y-8">
        <div className="space-y-4 lg:space-y-6">
          <div className="relative flex items-center justify-center gap-3 lg:min-h-[48px]">
            <div className="flex items-center gap-3">
              <Crown className="w-6 h-6 text-accent-primary lg:w-8 lg:h-8" />
              <h1 className="text-2xl font-bold lg:text-4xl text-accent-primary uppercase italic">{t('royale.title')}</h1>
              <button
                onClick={() => setShowHelp(true)}
                className="p-1.5 rounded-full bg-dark-secondary/50 hover:bg-dark-secondary transition-colors"
                aria-label={t('royale.aria.help')}
              >
                <HelpCircle className="w-5 h-5 text-text-secondary" />
              </button>
            </div>
          </div>

          {engine === 'node' && !isConnected && showReconnectNotice && (
            <div className="flex justify-center">
              <span role="status" className="rounded-full border border-white/10 bg-surface px-3 py-1 text-xs text-text-secondary">
                {t('realtime.reconnecting')}
              </span>
            </div>
          )}

          {showStats && (
            <div className="flex items-center justify-between gap-4 lg:hidden ">
              <div>
                <p className="text-text-secondary text-xs uppercase tracking-[0.2em]">{t('royale.bank')}</p>
                <p className="mt-2 text-3xl font-bold text-accent-primary inline-flex items-center gap-2">
                  <Gem className="w-6 h-6 text-accent-primary" />
                  {Number(totalBetValue || 0).toLocaleString()}
                </p>
              </div>

              <div className="text-right">
                {showTimer ? (
                  <div className="inline-flex flex-col items-end">
                    {timerLabel && <span className="text-text-secondary text-sm block">{timerLabel}</span>}
                    <span className="text-white font-bold text-4xl tracking-wide tabular-nums font-mono">
                      {timerValue}
                    </span>
                    <div className="mt-2 h-1 w-full rounded-full bg-white/20 overflow-hidden flex justify-end">
                      <div
                        className="h-full bg-white transition-all"
                        style={{ width: `${progressRatio * 100}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold ${statusClassName}`}>
                    {statusLabel}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start lg:gap-6">
          <div className="space-y-6 lg:space-y-8">
            {roundStatus === 'raffle' && rouletteRoundId && users.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                <RoyaleRoulette
                  users={users}
                  winner={winner}
                  currentUserId={currentUserId}
                  startKey={roundStatus === 'raffle' ? rouletteStartKey : 0}
                />
              </motion.div>
            )}
            {['result', 'finished'].includes(roundStatus) && (
              <RoyaleWinnerCard
                winner={winner}
                winTicket={resultData?.win_ticket}
                winAmount={winnerAmount}
                winPercent={winnerPercent}
                isCurrentUser={isCurrentUser}
                isParticipant={isParticipant}
                refundAmount={userRefund}
              />
            )}

            {isWaitingOtherTeam && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="hidden flex items-center gap-3 rounded-2xl border border-team-accent/30 bg-team-accent/10 px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
              >
                <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-team-accent/15">
                  <span className="absolute inset-0 rounded-xl bg-team-accent/20 animate-pulse" />
                  <Users className="relative h-4 w-4 text-team-accent" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">{t('royale.hints.waitingOtherTeamTitle')}</p>
                  <p className="text-[11px] text-text-secondary">{t('royale.hints.waitingOtherTeamSubtitle')}</p>
                </div>
              </motion.div>
            )}

            <RoyaleParticipantsShowcase
              users={showcaseUsers}
              teams={showcaseTeams}
              totalBet={showcaseTotalBet}
              monochrome={isRoundCompleted}
              highlightId={isRoundCompleted ? highlightId : ''}
              highlightIndex={highlightIndex}
              containerClassName="w-full aspect-square lg:aspect-[1.35/1]"
            />

            <div className="hidden lg:flex flex-wrap items-center justify-center gap-3">
              {actionButtons}
            </div>
          </div>

          <div className="mt-6 space-y-6 lg:mt-0">
            <div className="hidden lg:block space-y-3">
              {showStats && (
                <div className="flex items-center justify-between gap-4 pt-4">
                  <div className="min-w-0">
                    <p className="text-text-secondary text-xs uppercase tracking-[0.2em]">{t('royale.bank')}</p>
                    <p className="mt-2 text-2xl font-bold text-accent-primary inline-flex items-center gap-2 min-w-0">
                      <Gem className="w-5 h-5 text-accent-primary shrink-0" />
                      {Number(totalBetValue || 0).toLocaleString()}
                    </p>
                  </div>

                  <div className="min-w-0">
                    <div className="text-right min-h-[50px] flex flex-col justify-center">
                      {showTimer ? (
                        <div className="inline-flex flex-col items-end w-full">
                          {timerLabel && <span className="text-text-secondary text-sm block">{timerLabel}</span>}
                          <span className="text-white font-bold text-3xl tracking-wide tabular-nums font-mono">
                            {timerValue}
                          </span>
                          <div className="mt-2 h-1 w-full rounded-full bg-white/20 overflow-hidden flex justify-end">
                            <div
                              className="h-full bg-white transition-all"
                              style={{ width: `${progressRatio * 100}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold ${statusClassName}`}>
                          {statusLabel}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {isParticipant && userBetTotal > 0 && (
              <RoyaleUserShare
                avatar={userAvatar}
                name={userName}
                percent={userSharePercent}
              />
            )}

            <div className="hidden lg:block">
              {isGuest ? <button type="button" onClick={openLogin} className="gradient-border-cta w-full rounded-xl p-4 font-bold text-dark-primary">{t('guest.login')}</button> : <MobileBetSheet
                variant="inline"
                mode="royale"
                minBet={minBet}
                maxBet={maxBet}
                maxItems={maxItems}
                isDisabled={isBettingDisabled}
                isLoading={isBetting}
                isDataReady={isBetDataReady}
                balance={currentBalance}
                inventory={inventory}
                onSubmit={handleSubmitBet}
              />}
            </div>

            <RoyaleTeamsSummary teams={showcaseTeams} />

            <RoyaleBetsFeed
              bets={isRoundCompleted ? resultBets : bets}
              teams={showcaseTeams}
              totalBet={totalBetValue}
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-3 lg:hidden">
          {actionButtons}
        </div>
      </div>

      <TeamCapturePreview mode="classic" className="mx-auto mt-8 max-w-3xl" />

      <div className="fixed bottom-16 left-0 right-0 z-40 lg:hidden">
        <div className="container mx-auto px-4 py-4 space-y-3">
          <button
            onClick={handleOpenBetModal}
            disabled={isBettingDisabled}
            className={`w-full py-3 rounded-lg font-bold transition-opacity ${
              isBettingDisabled
                ? 'bg-surface text-text-secondary border border-white/10 cursor-not-allowed'
                : 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary hover:opacity-90'
            }`}
          >
            {isBettingDisabled ? t('royale.buttons.betsClosed') : t('royale.buttons.makeBet')}
          </button>
        </div>
      </div>

      <MobileBetSheet
        isOpen={isBetModalOpen}
        mode="royale"
        minBet={minBet}
        maxBet={maxBet}
        maxItems={maxItems}
        isDisabled={isBettingDisabled}
        isLoading={isBetting}
        isDataReady={isBetDataReady}
        inventory={inventory}
        balance={currentBalance}
        onClose={handleCloseBetModal}
        onSubmit={handleSubmitBet}
      />

      {isFairPlayOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-dark-primary rounded-2xl border border-white/10 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <h3 className="text-white font-semibold">{t('royale.fairPlay.title')}</h3>
              <button
                type="button"
                onClick={() => setIsFairPlayOpen(false)}
                className="p-2 text-text-secondary hover:text-white"
                aria-label={t('common.close')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs text-text-secondary">{t('royale.fairPlay.roundId')}</p>
                  <p className="text-sm text-white font-medium truncate">{formatValue(roundId)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopy(t('royale.fairPlay.roundId'), roundId)}
                  className="inline-flex items-center gap-1 text-xs text-accent-primary hover:text-accent-primary/80 transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs text-text-secondary">{t('royale.fairPlay.hash')}</p>
                  <p className="text-sm text-white font-medium truncate">{formatValue(roundHash)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopy(t('royale.fairPlay.hash'), roundHash)}
                  className="inline-flex items-center gap-1 text-xs text-accent-primary hover:text-accent-primary/80 transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title={t('royale.help.title')}
        steps={[
          t('royale.help.step1'),
          t('royale.help.step2'),
          t('royale.help.step3'),
          t('royale.help.step4'),
          t('royale.help.step5')
        ]}
      />
    </div>
  )
}

export default Royale
